import { Editor } from '@tinymce/tinymce-react';
import { selectFeatureFlags } from 'core/userContext';
import { MessageMedium } from 'modules/graphql/types';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import env from 'utils/env';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import {
  customElements,
  entities,
  extendedValidElements,
  TINY_MCE_IMAGE_FILE_TYPES,
  TINY_MCE_INVALID_ELEMENTS,
  validChildren,
} from '../../constants';
import { useWYSIWYGContext } from '../../contexts/WYSIWYGContext';
import {
  ImageModalType,
  useHandleImageModal,
  useReplaceContent,
  useStylesFullDoc,
} from '../../hooks';
import { AutoCompleteParam, useAutoCompleteParams } from '../../hooks/useAutoCompleteParams';
import { useHandleOpenModal } from '../../hooks/useHandleOpenModal';
import { useTinyMceVersion } from '../../hooks/useTinyMceVersion';
import { useTranslations } from '../../hooks/useTranslations';
import iterableFullpagePlugin from '../../plugins/iterableFullpage';
import { Modal } from '../../types/modal';
import {
  dynamicContentBuilderIcon,
  iterableCompareIcon,
  iterableMagicIcon,
  movableInkIcon,
  snippetIcon,
} from './icons';

// notes:
// https://www.tiny.cloud/docs/integrations/react/
// https://www.tiny.cloud/docs/tinymce/6/react-ref/
// https://www.tiny.cloud/docs/ui-components/autocompleter/
// https://www.tiny.cloud/blog/tinymce-toolbar/
// https://www.tiny.cloud/docs/ui-components/typesoftoolbarbuttons/

// Configuration Options (init keys)
// https://www.tiny.cloud/docs/configure/

// Toolbar Button Customization (Add, remove, reorder buttons)
// https://www.tiny.cloud/docs/tinymce/6/available-toolbar-buttons/
// Toolbar Formatting Options (Define custom block formats, font families, font sizes, etc.)
// https://www.tiny.cloud/docs/tinymce/6/user-formatting-options/
// Premium: PowerPaste
// https://www.tiny.cloud/docs/tinymce/6/introduction-to-powerpaste/

// Allow/Block list(s)
// https://www.tiny.cloud/docs/tinymce/6/content-filtering/#extended_valid_elements
// https://www.tiny.cloud/docs/tinymce/6/content-filtering/#invalid_elements
// https://www.tiny.cloud/docs/tinymce/6/content-filtering/#valid_elements

// Image upload
// https://www.tiny.cloud/docs/tinymce/6/file-image-upload/

// Custom Fonts
// https://www.tiny.cloud/blog/tinymce-custom-font-family/

// Full Page (deprecated)
// https://www.tiny.cloud/docs/plugins/opensource/fullpage/
// https://github.com/tinymce/tinymce-dist/tree/5.8.2/plugins/fullpage

/**
 * see src/modules/wysiwygEdtior/index.tsx -- WYSIWYGEditorProps for explanation;
 */
type TinyMCEEditorProps = {
  id: string;
  height: number | string;
  canUseAutoCompleteParams?: boolean;
  messageMedium?: MessageMedium;
};

export const TinyMCEEditor = ({
  id,
  height,
  canUseAutoCompleteParams = false,
  messageMedium,
}: TinyMCEEditorProps) => {
  const { t, ready } = useTranslations();
  const { WYSIWYGHTMLRepairTool, DynamicContentBuilder, MovableInk } =
    useSelector(selectFeatureFlags);

  const autoCompleteParamsObj = useAutoCompleteParams(canUseAutoCompleteParams, messageMedium);
  // use a useRef so that the getMatchedChars function maintains reference to the same object
  // (reference value) otherwise the will not get the update when useAutoCompleteParams resolves
  const autoCompleteParams = useRef<AutoCompleteParam[]>(autoCompleteParamsObj.params);

  const [showVisualAid, setShowVisualAid] = useLocalStorage('wysiwyg-show-visual-aid', true);
  const {
    initValue,
    onChange,
    contentValue,
    isFullHtmlDoc,
    canLoadHTML,
    htmlEditorRef,
    loadHtmlFileInputRef,
  } = useWYSIWYGContext();
  const { bodyClasses, headStyles, headStylesheets } = useStylesFullDoc();

  const replaceContent = useReplaceContent();
  const handleConfirmHtmlCleanup = useHandleOpenModal(Modal.ConfirmCleanup);
  const handleViewHtmlDiff = useHandleOpenModal(Modal.ViewChanges);
  const handleOpenSnippetSelector = useHandleOpenModal(Modal.SnippetSelector);
  const handleOpenDynamicContentBuilder = useHandleOpenModal(Modal.DynamicContentBuilder);
  const handleImageModal = useHandleImageModal();

  const showDiffTools = WYSIWYGHTMLRepairTool;
  const tinyMceVersion = useTinyMceVersion();

  useEffect(() => {
    autoCompleteParams.current = autoCompleteParamsObj.params;
  }, [autoCompleteParamsObj]);

  // wait for translations to be ready, otherwise button renter with the button keys and not the intended text
  if (!ready) return null;
  return (
    <Editor
      id={id}
      initialValue={initValue}
      init={{
        allow_conditional_comments: true,
        automatic_uploads: true,
        branding: false,
        custom_elements: customElements,
        default_link_target: '_blank',
        extended_valid_elements: extendedValidElements,
        // visualblocks_default_state: true,
        // end_container_on_empty_block: true,
        body_class: bodyClasses,
        content_css: headStylesheets,
        contextmenu: `iterableImageMenuItem link table`,
        content_style: headStyles,
        convert_urls: false, // do not automatically 'correct' urls (false allows us to use snippets in the url)
        entities,
        forced_root_block: 'div', // 'p' by default
        // language: 'zh_CN', // for member UI localization (not template/content localization)
        // directionality: 'rtl' // for member UI localization (not template/content localization)
        height,
        images_file_types: TINY_MCE_IMAGE_FILE_TYPES,
        invalid_elements: TINY_MCE_INVALID_ELEMENTS,
        menubar: false,
        // premium: advcode, powerpaste
        plugins: `advcode preview powerpaste link image table lists directionality wordcount${
          isFullHtmlDoc ? ' iterablefullpage' : ''
        }`,
        // bullist: requires lists plugin
        // code: requires code plugin (or advcode)
        // image: requires image plugin
        // link: requires link plugin
        // ltr: requires directionality plugin
        // numlist: requires lists plugin
        // pastetext: requires paste plugin (or powerpaste)
        // preview: requires preview plugin
        // rtl: requires directionality plugin
        // table: requires table plugin
        toolbar: [
          `code | preview | undo redo | pastetext | link | iterableImageButton${
            MovableInk ? ' iterableMovableInkButton' : ''
          } | iterableSnippetButton${
            DynamicContentBuilder ? ' | dynamicContentBuilderButton' : ''
          } | table hr | alignleft aligncenter alignright alignjustify | forecolor backcolor${
            canLoadHTML ? ' | iterableLoadHTMLButton' : ''
          }${showDiffTools ? ' | iterableHtmlDiffViewerButton iterableHtmlCleanupButton' : ''}`,
          'bold italic | removeformat | numlist bullist | outdent indent | blockquote | styles | fontfamily | fontsize | ltr rtl | iterableVisualAidToggle',
        ],
        toolbar_mode: 'wrap', // wrap buttons whenever they exceed the page width
        paste_tab_spaces: 2,
        // https://www.tiny.cloud/docs/tinymce/6/powerpaste-options/#powerpaste_googledocs_import
        // powerpaste_googledocs_imports: () => new Promise((resolve) => {
        //   // use a native confirm dialog to prompt the user to choose between clean and merge
        //   // eslint-disable-next-line
        //   if (confirm('Would you like to keep googledocs formatting?')) {
        //     resolve('merge');
        //   } else {
        //     resolve('clean');
        //   }
        // }),
        powerpaste_googledocs_imports: 'prompt',
        powerpaste_html_import: 'prompt',
        powerpaste_word_import: 'prompt',
        setup: (editor) => {
          window.tinymce.PluginManager.add('iterablefullpage', iterableFullpagePlugin);

          // snippet/data field typeahead
          const onAction = (autocompleteApi: any, rng: Range, newValue: string) => {
            editor.selection.setRng(rng);
            editor.insertContent(newValue);
            autocompleteApi.hide();
          };

          const getMatchedChars = (pattern: string) => {
            return autoCompleteParams.current.filter((char) => {
              return char.text.includes(pattern);
            });
          };

          if (canUseAutoCompleteParams) {
            /* An autocompleter that allows you to insert special characters */
            editor.ui.registry.addAutocompleter('iterableAutoCompleteParams', {
              // The character(s) to trigger the autocompleter.
              // @ts-ignore
              trigger: '{{',
              // The minimum number of characters that must be typed before the autocompleter will trigger
              // (excluding the trigger char).
              minChars: 0, // trigger the list immediately when the '{' character is typed
              // The number of columns to show. If set to 1 column, then icons and text are displayed,
              // otherwise only icons are displayed.
              columns: 1,
              // A function invoked when a fetched item is selected.
              onAction,
              // A function that is passed the current matched text pattern, the maximum number of
              // expected results and any additional fetch options. The function should return a Promise
              // containing matching results.
              fetch: (pattern: string) => {
                return new Promise((resolve: any) => {
                  const results = getMatchedChars(pattern).map((char) => {
                    return {
                      type: 'autocompleteitem',
                      value: char.value,
                      text: char.text,
                      icon: undefined,
                    };
                  });
                  resolve(results);
                });
              },
            });
          }

          editor.ui.registry.addToggleButton('iterableVisualAidToggle', {
            icon: 'select-all',
            tooltip: t('tooltip.toggleVisualAid'),
            onAction: (api) => {
              const toggleValue = !api.isActive();
              editor.execCommand('mceToggleVisualAid');
              api.setActive(toggleValue);
              setShowVisualAid(toggleValue);
            },
            // @ts-ignore
            onSetup: (api) => {
              // set initial value
              api.setActive(showVisualAid);
            },
          });

          if (canLoadHTML) {
            editor.ui.registry.addButton('iterableLoadHTMLButton', {
              icon: 'browse',
              text: t('button.loadHtml'),
              tooltip: t('tooltip.loadHtml'),
              onAction: () => {
                loadHtmlFileInputRef.current?.click();
              },
            });
          }

          if (showDiffTools) {
            editor.ui.registry.addIcon('iterableMagic', iterableMagicIcon);
            editor.ui.registry.addIcon('iterableCompare', iterableCompareIcon);
            editor.ui.registry.addButton('iterableHtmlCleanupButton', {
              icon: 'iterableMagic',
              text: t('button.htmlCleanup'),
              tooltip: t('tooltip.htmlCleanup'),
              onAction: handleConfirmHtmlCleanup,
            });
            editor.ui.registry.addButton('iterableHtmlDiffViewerButton', {
              icon: 'iterableCompare',
              text: t('button.htmlDiff'),
              tooltip: t('tooltip.htmlDiff'),
              onAction: handleViewHtmlDiff,
            });
          }

          editor.ui.registry.addButton('iterableImageButton', {
            icon: 'image',
            text: t('button.images'),
            tooltip: t('tooltip.imageButton'),
            onAction: () => {
              // Determine if an image is selected
              const node = editor.selection.getNode();
              handleImageModal(node.tagName === 'IMG' ? (node as HTMLImageElement) : undefined);
            },
          });
          editor.ui.registry.addMenuItem('iterableImageMenuItem', {
            icon: 'image',
            text: t('menu.image'),
            onAction: () => {
              // Determine if an image is selected
              const node = editor.selection.getNode();
              handleImageModal(node.tagName === 'IMG' ? (node as HTMLImageElement) : undefined);
            },
          });

          editor.ui.registry.addContextMenu('iterableImageMenuItem', {
            update: (element) => (!element.getAttribute('src') ? '' : 'iterableImageMenuItem'),
          });

          if (MovableInk) {
            editor.ui.registry.addIcon('movableInk', movableInkIcon);
            editor.ui.registry.addButton('iterableMovableInkButton', {
              icon: 'movableInk',
              text: t('button.movableInk'),
              tooltip: t('tooltip.movableInkButton'),
              onAction: () => {
                // Determine if an image is selected
                const node = editor.selection.getNode();
                handleImageModal(
                  node.tagName === 'IMG' ? (node as HTMLImageElement) : undefined,
                  ImageModalType.MovableInk,
                );
              },
            });
          }

          editor.ui.registry.addIcon('iterableSnippet', snippetIcon);
          editor.ui.registry.addButton('iterableSnippetButton', {
            icon: 'iterableSnippet',
            text: t('button.snippets'),
            tooltip: t('tooltip.snippetButton'),
            onAction: handleOpenSnippetSelector,
          });

          if (DynamicContentBuilder) {
            editor.ui.registry.addIcon('dynamicContentBuilder', dynamicContentBuilderIcon);
            editor.ui.registry.addButton('dynamicContentBuilderButton', {
              icon: 'dynamicContentBuilder',
              text: t('button.dynamicContentBuilder'),
              tooltip: t('tooltip.dynamicContentBuilderButton'),
              onAction: handleOpenDynamicContentBuilder,
            });
          }
        },
        // svg: true,
        // svgFilters: true,
        valid_children: validChildren,
        // valid_elements : '+*[*]', // allow all tags
        visual: showVisualAid, // default for visual aids (can be toggled with iterableVisualAidToggle toolbar button)
      }}
      onEditorChange={onChange}
      onInit={(evt, editor) => {
        htmlEditorRef.current = editor;
      }}
      onDblclick={(e) => {
        if (e.target.tagName === 'IMG') {
          handleImageModal(e.target);
        }
      }}
      onSetContent={(setContentData: any) => {
        const { iterableTrigger }: Record<string, any> = setContentData;

        if (iterableTrigger === 'advcode' && isFullHtmlDoc) {
          // Replaces all content (see note above on the renderTiny useEffect)
          const editorValue = htmlEditorRef?.current?.getContent() || '';
          replaceContent(editorValue);
        }
      }}
      tinymceScriptSrc={`${env.publicPath}wysiwyg/${tinyMceVersion}/tinymce.min.js`}
      value={contentValue}
    />
  );
};
